import * as React from "react";
import {
  IFree30Subject,
  IFree30SubjectsResponse
} from "./free30-sequence.types";

const API_URL = process.env.REACT_APP_COACH_PERF_API_URL || "";
const API_KEY = process.env.REACT_APP_COACH_PERF_API_KEY || "";

export interface IUseFree30Subjects {
  subjects: IFree30Subject[] | null;
  loading: boolean;
  error: string | null;
}

/**
 * Fetch the Free 30 guests for a gym/month, optionally scoped to one coach.
 * `enabled` gates the fetch so we only load when the drill-down is opened.
 */
export function useFree30Subjects(
  subdomain: string,
  month: string,
  coach: string | null,
  enabled: boolean
): IUseFree30Subjects {
  const [subjects, setSubjects] = React.useState<IFree30Subject[] | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!enabled || !API_URL || !API_KEY || !subdomain || !month)
      return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ subdomain, month, limit: "100" });
    if (coach) params.set("coach", coach);
    const url = `${API_URL}/evidence/free30/subjects?${params.toString()}`;

    fetch(url, { headers: { "X-API-Key": API_KEY } })
      .then(async res => {
        const body = await res.json().catch(() => null);
        if (!res.ok)
          throw new Error((body && body.message) || `HTTP ${res.status}`);
        return body as IFree30SubjectsResponse;
      })
      .then(body => {
        if (!cancelled) setSubjects(body.subjects || []);
      })
      .catch(e => {
        if (!cancelled) setError(e.message || "Failed to load guests");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [subdomain, month, coach, enabled]);

  return { subjects, loading, error };
}
